(() => {
  if (customElements.get('ready-to-ship-pages')) return;

  class ReadyToShipPages extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      queueMicrotask(() => this.initialize());
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    async initialize() {
      if (!this.isConnected) return;
      const signal = this.controller.signal;
      this.grid = this.querySelector('.grid');
      this.pagination = this.querySelector('[data-ready-pagination]');
      if (!this.grid || !this.pagination) return;
      this.baseUrl = new URL(window.location.href);
      this.baseUrl.searchParams.delete('section_id');
      const sourcePage = Number(this.dataset.sourcePage);
      const sourcePages = Number(this.dataset.sourcePages);
      const requestedPage = Math.max(1, Number(this.baseUrl.searchParams.get('page')) || 1);
      const gcd = (a, b) => b ? gcd(b, a % b) : a;
      const rowSize = this.dataset.columns.split(',').map(Number)
        .reduce((a, b) => a * b / gcd(a, b), 1);
      this.pageSize = Math.max(rowSize, Math.floor(Number(this.dataset.pageSize) / rowSize) * rowSize);
      const chunks = new Map([[sourcePage, [...this.grid.querySelectorAll('[data-ready-variant-id]')]]]);
      const noResults = this.grid.querySelector('.no-results')?.cloneNode(true);
      const pending = Array.from({ length: sourcePages }, (_, i) => i + 1)
        .filter(page => page !== sourcePage);
      this.setAttribute('aria-busy', 'true');

      try {
        const worker = async () => {
          while (pending.length) {
            const page = pending.shift();
            const url = new URL(this.baseUrl);
            url.searchParams.set('page', page);
            url.searchParams.set('section_id', this.dataset.section);
            const response = await fetch(url, { signal });
            if (!response.ok) throw new Error('Collection page unavailable');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const section = [...doc.querySelectorAll('ready-to-ship-pages')]
              .find(element => element.dataset.section === this.dataset.section);
            if (!section) throw new Error('Collection section unavailable');
            chunks.set(page, [...section.querySelectorAll('[data-ready-variant-id]')]
              .map(card => document.importNode(card, true)));
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, pending.length) }, worker));
        if (signal.aborted || !this.isConnected) return;
        const seen = new Set();
        this.cards = [...chunks.entries()].sort(([a], [b]) => a - b).flatMap(([, cards]) => cards)
          .filter(card => {
            const id = card.dataset.readyVariantId;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
        this.pageCount = Math.max(1, Math.ceil(this.cards.length / this.pageSize));
        if (!this.cards.length && noResults) this.grid.replaceChildren(noResults);
        this.render(requestedPage);
        this.addEventListener('click', event => {
          const link = event.target.closest('[data-ready-page]');
          if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const page = Number(link.dataset.readyPage);
          window.history.pushState(null, '', this.pageUrl(page));
          this.render(page);
          const navHeight = this.closest('collection-component').querySelector('[data-collection-nav]')?.offsetHeight || 0;
          window.theme?.scrollTo?.(this.getBoundingClientRect().top - navHeight);
          this.pagination.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
        }, { signal });
        if (!this.closest('collection-component').querySelector('collection-filters-form')) {
          window.addEventListener('popstate', () => {
            this.render(Math.max(1, Number(new URL(window.location.href).searchParams.get('page')) || 1));
          }, { signal });
        }
        this.dataset.ready = 'true';
      } catch (error) {
        if (error.name !== 'AbortError') this.dataset.ready = 'fallback';
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    pageUrl(page) {
      const url = new URL(this.baseUrl);
      if (page === 1) url.searchParams.delete('page');
      else url.searchParams.set('page', page);
      return url.pathname + url.search + url.hash;
    }

    render(requestedPage) {
      const page = Math.min(this.pageCount, requestedPage);
      if (this.cards.length) this.grid.replaceChildren(...this.cards.slice((page - 1) * this.pageSize, page * this.pageSize));
      this.dataset.currentPage = page;
      this.dataset.totalCards = this.cards.length;
      this.pagination.replaceChildren();
      if (this.pageCount === 1) return;
      const nav = document.createElement('nav');
      nav.className = 'pagination-custom';
      const link = (number, text, className) => {
        const a = document.createElement('a');
        a.className = className;
        a.href = this.pageUrl(number);
        a.dataset.readyPage = number;
        a.textContent = text;
        return a;
      };
      if (page > 1) nav.append(link(page - 1, this.dataset.previous, 'pagination-custom__prev'));
      const inner = document.createElement('div');
      inner.className = 'pagination-custom__inner';
      const numbers = new Set([1, this.pageCount, page - 1, page, page + 1]);
      let previous = 0;
      for (const number of [...numbers].filter(n => n >= 1 && n <= this.pageCount).sort((a, b) => a - b)) {
        if (previous && number - previous > 1) {
          const gap = document.createElement('span');
          gap.className = 'pagination-custom__page';
          gap.textContent = '…';
          inner.append(gap);
        }
        if (number === page) {
          const current = document.createElement('span');
          current.className = 'pagination-custom__page pagination-custom__page--active';
          current.setAttribute('aria-current', 'page');
          current.tabIndex = -1;
          current.textContent = number;
          inner.append(current);
        } else inner.append(link(number, number, 'pagination-custom__page'));
        previous = number;
      }
      nav.append(inner);
      if (page < this.pageCount) nav.append(link(page + 1, this.dataset.next, 'pagination-custom__next'));
      this.pagination.append(nav);
    }
  }

  customElements.define('ready-to-ship-pages', ReadyToShipPages);
})();
